/*********************************************************
 * Imports
 *********************************************************/
import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";

import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from "react-intl";

import { MapUtilitiesTypes } from '../MapUtilitiesMenu/enums';
import { createSelector } from 'reselect'
import { validateJsonObject, getUnitsNaturalOrder } from "Core/data/Helpers";
import { TypeObjectMap } from "../../../../../components/Map/providers/enums";

import { unitSelectors } from 'Core/realtime/redux/unitSlice';
import { driverSelectors } from 'Core/realtime/redux/driverSlice';
import { useDataGridThrottler } from 'Core/hooks';
import './style.scss'

/*********************************************************
 * Actions
 *********************************************************/
import {
  centerOnUnit,
  setHiddenGroups,
  /* catalogLoad,  */
  getPreferences,
  savePreference,
  setFollowUnit,
  selectModuleMapSettings,
  getVehicleListMarkerDetailId,
  clearRedux,
  setHiddenTags,
  setShowTagFIlterApplied
} from "Redux/actions";

/*********************************************************
 * Components / Controls
 *********************************************************/
import { NotificationManager } from "Components/Notifications";
import GroupsList from '../../../../../components/GroupsList';
import { Grid } from '@mui/material';

/*********************************************************
 * Styles
 *********************************************************/
//import './style.scss';

import Preferences from 'Core/containers/Preferences'

/********************************************************
 * Remove the true value, *** Remove this function
 * @param {string} field 
 ********************************************************/
const validateIntegrityField = (field) => {
  return (field + '').toString() != 'true' ? field : ''
}

var varLocale = localStorage.getItem('lan');

/*********************************************************
 * Global Vars
*********************************************************/
let hiddenTagsPreferences = []

const getUnitsCatalog = createSelector([state => unitSelectors.selectAll(state)], getUnitsNaturalOrder);
const getVehicleTags = (state) => state.securityRedux?.user?.tagIds;
const getDrivers = (state) => driverSelectors.selectEntities(state);

/*********************************************************
 * Get information from pagination elements
 * Uses Map for O(1) driver lookups and Set for O(1) hidden tag checks
*********************************************************/
const getData = createSelector(
  [getUnitsCatalog, getVehicleTags, getDrivers],
  (units, tagsPermissions, drivers) => {
    let unitList = [];
    const tagsEnableForUser = validateJsonObject(tagsPermissions) || [];
    let vehicleTags = tagsEnableForUser?.includes(-99) ? [] : tagsEnableForUser;
    try {
      units.forEach((unit) => {
        if (unit.id == "-1" || (!unit.id && !unit.CUnits_id)) return

        let _deviceTags = [];
        const driver = unit?.driverId ? drivers[unit.driverId] : null;

        if (unit && unit?.tags && Array.isArray(unit?.tags) && unit.tags?.length > 0) {
          unit.tags?.map(group => {
            let groupToPush = {}
            groupToPush.id = group?.id || -1
            _deviceTags.push(groupToPush)
          })
        }

        if (!unit?.tags || unit?.tags?.length == 0 || !Array.isArray(unit?.tags) || (_deviceTags?.length == 1 && _deviceTags[0]?.id == -1)) {
          //add the default group 
          unit = {
            ...unit,
            tags: [{
              color: "000000",
              hasChildren: false,
              id: -1,
              label: "Untagged",
              parentId: null,
              path: ""
            }]
          }
          _deviceTags?.length == 0 && _deviceTags.push({ id: -1 })
        }
        let hideUnitByTag = false
        if (hiddenTagsPreferences?.length > 0) {
          let countsItemsToHide = _deviceTags.length;
          _deviceTags.map((group) => {
            const removeGroup = hiddenTagsPreferences.find((hiddenG) => hiddenG == group.id)
            if (removeGroup >= 0) {
              countsItemsToHide--;
            }
          })
          if (countsItemsToHide == 0) hideUnitByTag = true
        }

        const driverName = driver ? validateIntegrityField(driver?.name) : '';
        //remove tags permisions with vehicleTags
        _deviceTags = vehicleTags?.length == 0 ? _deviceTags : _deviceTags.filter(tag => vehicleTags?.some(tagId => tagId === tag?.id));

        //filter unit for existing tag
        unit = {
          ...unit,
          tags: vehicleTags?.length == 0 ? unit.tags : unit.tags.filter(tag => vehicleTags?.some(tagId => tagId === tag?.id))
        }

        // if the unit no have tags or none is the group "Untagged", it is omitted
        if (!unit?.tags?.length && !unit?.tags?.some(tag => tag.id === -1)) return; // No is entered on the list of vehicle because no have permisions

        const item = {
          id: unit.id,
          label: unit?.label,
          haveLatLng: unit?.installed, // this vehicle no have lat and lng, cant show on map
          installed: unit?.installed || false,
          driverId: unit?.driverId,
          driverName: unit?.driverId > 0 ? driverName : '',
          deviceTags: _deviceTags,
          tags: unit?.tags,
          hideUnitByTag: hideUnitByTag
        }
        unitList.push(item);
      });
    } catch (e) {
      console.log('e', e)
      NotificationManager.error('Error', `Error /modules/realtimeMaps/ to create show vehicles - ${e}`, 3000, null, null, 'error');
    }

    return unitList
  }
)

const EMPTY_ARRAY = [];

/*********************************************************
 * Author:  Walter Paz Londoño
 * Date:    Dec 2020
 * Module:  Realtime Maps
 * 
 * Class associated to the google map provider
 ********************************************************/
const MapUtilitiesVehicleList = (props) => {
  const { messages, locale } = useIntl();
  const dispatch = useDispatch()
  const preferencesRef = useRef()
  const [performUpdate, setPerformUpdate] = useState(false)
  const [preferencesItems, setPreferencesItems] = useState()
  const [highlightId, setHighlightId] = useState(0)
  const [selectedAll, setSelectedAll] = useState(false);

  const preferencesKey = 'realtimeMaps';

  const itemRef = useRef(null);

  /*********************************************************
   * Selectors
  *********************************************************/

  const loadTags = useSelector(state => state.catalogsRedux.loadingTags);

  const hiddenTagsIds = useSelector(state => state.realtimeMapUtilitiesVehiclesListRedux.hiddenTagsIds) || EMPTY_ARRAY;

  const authenticatedId = useSelector(state => state.securityRedux.authenticatedId);

  const preferenceResultUpdate = useSelector(state => state.preferencesRedux.preferenceResultUpdate);
  const centerUnitId = useSelector(state => state.realtimeMapRedux.centerUnitId);
  const followUnit = useSelector(state => state.realtimeMapRedux.followUnit);
  //const getMarkerDetailsId = useSelector(state => state.infoBubbleRedux.getMarkerDetailsId);
  const selectMarkerDetail = useSelector(state => state.infoBubbleRedux.selectMarkerDetail)
  const returnServer = useSelector(state => state.preferencesRedux.returnServer);
  const preferencesUser = useSelector(state => state.preferencesRedux.preferencesUser);

  /*********************************************************
   * use compute data to sync values showing in table
  *********************************************************/
  const computedData = useSelector(state => getData(state) || []);
  const propertiesToValidate = useMemo(() => ["label", "installed", "driverId", "driverName", "hideUnitByTag", "tags"], []);

  // Hook handles both Throttled Arrays for `<Map/>` and imperative delta diffing for markers via `handleMapUpdates`
  const listVehicles = useDataGridThrottler(
    computedData,
    1000,
    () => { },
    "id",
    propertiesToValidate
  );

  useEffect(() => {
    return () => {
      dispatch(clearRedux("INFO_BUBBLE",
        {
          selectMarkerDetail: {
            vehicleListMarkerDetailId: null,
            markerDetailIndex: 0
          }
        }
      ))
    }
  }, [])

  useEffect(() => {
    if (varLocale != locale) {
      dispatch(selectModuleMapSettings(MapUtilitiesTypes.Default));
      varLocale = locale
    }
  }, [locale])

  useEffect(() => {
    if (preferencesUser) {
      let preferencesKeyTemp = preferencesUser.find(x => x.preferenceKey === preferencesKey);
      let preferencesJSON = preferencesKeyTemp && preferencesKeyTemp.preferenceValue != "" && preferencesKeyTemp.preferenceValue || "{}";
      preferencesJSON = JSON.parse(preferencesJSON);

      setPreferencesItems(preferencesJSON)
    }
  }, [returnServer]);



  /*********************************************************
   * Load the follow unit in vehicle list
   *********************************************************/
  useEffect(() => {
    if (followUnit && centerUnitId != highlightId) {
      setHighlightId(centerUnitId)
    } else if (highlightId > 0 && !followUnit) {
      setHighlightId(0)
    }
  }, [centerUnitId, followUnit])



  useEffect(() => {
    if (selectMarkerDetail?.markerDetailIndex > 0) {
      try {
        //assing the reference per event click
        const item = itemRef?.current;
        let highlight = false
        if (highlightId != item?.id || (selectMarkerDetail?.vehicleListMarkerDetailId != item?.id && !highlightId)) {
          setHighlightId(item?.id)
          highlight = true
        } else {
          itemRef.current = null
          setHighlightId(0)
          dispatch(setFollowUnit(false))
          return
        }

        //if the unit not show display unit
        const getObjectFromMap = props?.mapRef?.current?.getObject(TypeObjectMap.Marker, item?.id);
        if (getObjectFromMap && (!getObjectFromMap?.enable || getObjectFromMap?.hideMarker)) {
          getObjectFromMap.enable = true;
          getObjectFromMap.hideMarker = false;
        }

        dispatch(centerOnUnit(item?.id, highlight));
        // Always show the modal immediately — showMarkerModal already
        // guards against duplicate processing via _.isEqual, and the
        // overlay reuse path handles switching between markers instantly.
        // The previous condition (gated by getOpenInfoWindow) skipped
        // this call when a modal was already open, forcing the slower
        // async useEffect path in mapsController.
        if (props?.mapRef?.current) {
          props.mapRef.current.showInfoWindow(item?.id);
        }

      } catch (e) {
        NotificationManager.error('Error', `Error onItemClicked in vehicle List - ${e}`, 3000, null, null, 'error');
        itemRef.current = null
      }
    }
    itemRef.current = null
  }, [selectMarkerDetail?.markerDetailIndex])

  /*********************************************************
   * filter the list of vehicles in view
   * FIX: Added proper dependency array to prevent recalc on every render
   *********************************************************/
  /* const unitTags = useMemo(() => {
    if (!haveUntagged && (!tagsVehicleList?.length || !processedData?.length)) {
      return EMPTY_ARRAY;
    }

    const arrayUntagged = {
      id: -1,
      name: untaggedLabel,
      visible: true,
      open: true,
      color: '#000000',
      unitgroupname: 'None',
      unitgroupcolor: '#000000',
      path: null,
      items: []
    };

    let vehicleTagList = [
      ...tagsVehicleList,
      arrayUntagged
    ];

    try {
      vehicleTagList = addVehiclesGroup(vehicleTagList)
      let listDevicesTags = createUnitTags(processedData, vehicleTagList)

      // Apply hidden/condensed preferences inline (replaces getTagListOrder)
      if (preferencesItems?.hiddenTagsOnPreference?.length || preferencesItems?.condensedUnitTagIds?.length) {
        const hiddenPrefSet = new Set(preferencesItems.hiddenTagsOnPreference || []);
        const condensedSet = new Set(preferencesItems.condensedUnitTagIds || []);

        listDevicesTags = listDevicesTags.map(dg => ({
          ...dg,
          visible: hiddenPrefSet.has(dg.id) ? false : dg.visible,
          open: condensedSet.has(dg.id) ? false : dg.open,
        }));
      }

      // Remove groups without units
      listDevicesTags = listDevicesTags.filter((item) => item.items.length > 0)

      // Sort alphabetically
      listDevicesTags.sort((a, b) =>
        (a.name ?? '').toString().toLowerCase().localeCompare(
          (b.name ?? '').toString().toLowerCase()
        )
      );

      // Put untagged in top of the list and adding lan
      const untaggedIndex = listDevicesTags.findIndex((item) => item?.id === -1);
      if (untaggedIndex > 0) {
        const [untagged] = listDevicesTags.splice(untaggedIndex, 1);
        listDevicesTags.unshift({ ...untagged, name: untaggedLabel });
      }

      return listDevicesTags;
    } catch (e) {
      NotificationManager.error('Error', `Error startArrayGroupsElements to create Groups - ${e}`, 3000, null, null, 'error');
    }

    return vehicleTagList;
  }, [haveUntagged, tagsVehicleList, processedData, preferencesItems, untaggedLabel]); */

  /*********************************************************
   * Perform the update when the check is clicked
   *********************************************************/
  const onItemClicked = useCallback((item) => {
    if (itemRef.current != null) {
      return
    }
    itemRef.current = item;
    dispatch(getVehicleListMarkerDetailId())
  }, [dispatch]);

  /*********************************************************
   * Get the preference when user update the view
   *********************************************************/
  useEffect(() => {
    if (!preferenceResultUpdate) {
      dispatch(getPreferences({ entityName: "User", entityId: authenticatedId }));
    }
  }, [preferenceResultUpdate])

  /*********************************************************
   * Order the units elements from name
   *********************************************************/
  const getTagListOrder = (devicesTags) => {
    if (preferencesItems?.hiddenTagsOnPreference?.length) {
      devicesTags = devicesTags?.map(dg => {
        if (preferencesItems?.hiddenTagsOnPreference?.includes(dg?.id)) {
          return {
            ...dg,
            visible: false
          }
        }
        return dg;
      });
    }

    if (preferencesItems?.condensedUnitTagIds?.length) {
      devicesTags = devicesTags?.map(dg => {
        if (preferencesItems?.condensedUnitTagIds?.includes(dg?.id)) {
          return {
            ...dg,
            open: false
          }
        }
        return dg;
      });
    }

    let arrayGroupFilter = [...devicesTags];

    //Hide checkbox items into the list
    // hiddenVehicleList?.map((hidden) => {
    //   const findElement = arrayGroupFilter.find((tag) => hidden.hiddenGroupIds == tag.id)
    //   if (findElement) findElement.visible = hidden.visible
    // });

    //remove duplicates
    arrayGroupFilter.sort((a, b) => { //Order by Alphabetically
      if (a.name > b.name) return 1;
      if (b.name > a.name) return -1;
      return 0;
    })

    return arrayGroupFilter
  }

  /*********************************************************
   * Build tag groups directly from listVehicles
   * Each vehicle already contains full tag data in its tags array
   *********************************************************/
  const unitTags = useMemo(() => {
    if (!listVehicles || listVehicles.length === 0) return [];

    const tagMap = new Map();

    listVehicles.forEach(vehicle => {
      if (vehicle.id === -1) return;

      const vehicleTags = vehicle.tags && Array.isArray(vehicle.tags) && vehicle.tags.length > 0
        ? vehicle.tags
        : [{ id: -1, label: 'Untagged', color: '000000', path: '' }];

      const driverLabel = vehicle.driverName ? ` (${vehicle.driverName})` : '';

      vehicleTags.forEach(tag => {
        const tagId = tag.id ?? -1;
        const tagIdKey = String(tagId);

        if (!tagMap.has(tagIdKey)) {
          const color = tag.color?.includes?.('#') ? tag.color : `#${tag.color || '000000'}`;
          tagMap.set(tagIdKey, {
            id: tagId,
            color,
            unitgroupcolor: color,
            name: Number(tagId) === -1 ? messages['untagged'] : (tag.label || ''),
            unitgroupname: Number(tagId) === -1 ? 'None' : (tag.label || ''),
            path: tag.path ?? null,
            visible: true,
            open: true,
            items: []
          });
        }

        const group = tagMap.get(tagIdKey);
        if (!group.items.some(item => item.id === vehicle.id)) {
          group.items.push({
            id: vehicle.id,
            label: vehicle.label,
            name: vehicle.label + driverLabel,
            driver: vehicle.driverName || '',
            highlight: false,
            noClickEvent: !vehicle.haveLatLng
          });
        }
      });
    });

    let listDevicesTags = Array.from(tagMap.values());
    listDevicesTags = getTagListOrder(listDevicesTags);

    // Remove empty groups
    listDevicesTags = listDevicesTags.filter(item => item.items.length > 0);

    // Sort alphabetically
    listDevicesTags.sort((a, b) => {
      const aName = a.name?.toString()?.toLowerCase() || '';
      const bName = b.name?.toString()?.toLowerCase() || '';
      if (aName > bName) return 1;
      if (bName > aName) return -1;
      return 0;
    });

    // Move "Untagged" to the top
    const untaggedIdx = listDevicesTags.findIndex(item => Number(item.id) === -1);
    if (untaggedIdx > 0) {
      const [untagged] = listDevicesTags.splice(untaggedIdx, 1);
      listDevicesTags.unshift(untagged);
    }

    return listDevicesTags;
  }, [listVehicles, preferencesItems, messages]);

  /*********************************************************
   * Update the view when the control perform action
   *********************************************************/
  const onActionsClicked = useCallback(() => {
    setPerformUpdate(prev => !prev)
  }, []);

  const onExpanded = useCallback((groupId, expanded) => {
    setPerformUpdate(prev => !prev);

    const condensedUnitTagIds = unitTags
      .filter(el => el.id === groupId ? !expanded : !el.open)
      .map(el => el.id);

    const preferenceValue = { ...preferencesItems, condensedUnitTagIds };
    dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: preferencesKey, preferenceValue }));
    setPreferencesItems(preferenceValue)
  }, [unitTags, preferencesItems, authenticatedId, dispatch]);

  /*********************************************************
   * Shows or hide units into the realtime maps
   *********************************************************/
  const onTagCheckChanged = useCallback((tagId, checked) => {
    setPerformUpdate(prev => !prev);

    const hiddenUnitTagIds = unitTags
      .filter(el => el.id === tagId ? !checked : !el.visible)
      .map(el => el.id);

    dispatch(setHiddenTags(hiddenUnitTagIds));
    if (hiddenUnitTagIds.length > 0) {
      dispatch(setShowTagFIlterApplied(true));
    }
    
    const preferenceValue = { ...preferencesItems, hiddenTagsOnPreference: hiddenUnitTagIds };
    dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: preferencesKey, preferenceValue }));
    setPreferencesItems(preferenceValue)
  }, [unitTags, preferencesItems, authenticatedId, dispatch]);

  const onSelectedTags = useCallback((value) => {
    const excludeItems = value ? [] : unitTags.map(item => item.id);
    setSelectedAll(value);

    dispatch(setHiddenTags(excludeItems));

    const preferenceValue = { ...preferencesItems, hiddenTagsOnPreference: excludeItems };
    dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: preferencesKey, preferenceValue }));
    setPreferencesItems(preferenceValue);
  }, [unitTags, preferencesItems, authenticatedId, dispatch]);

  return (
    <Preferences
      ref={preferencesRef}
      preferencesKey="realtimeMaps"
      onInitialPreferences={(paramPreferences) => {
        if (paramPreferences) {
          if (!preferencesItems) {
            setPreferencesItems(paramPreferences)
          }
        }
      }}
    >
      {
        <Grid container spacing={1}>
          <Grid item xs={5}>
            <h3 style={{ margin: 0 }}>{messages['maputilities_vehicles']}</h3>
          </Grid>
          <Grid item xs={7} style={{ whiteSpace: "nowrap" }}>
            <label
              style={{ textDecoration: 'underline', color: '#4b4b4b', cursor: 'pointer' }}
              className='vehicleList-showall-value'
              onClick={() => {
                onSelectedTags(true)
              }}
            >
              {messages['maputilities_vehiclelist_showAll']}
            </label>
            <label style={{ padding: 5 }}>{" | "}</label>
            <label
              style={{ textDecoration: 'underline', color: '#4b4b4b', cursor: 'pointer' }}
              className='vehicleList-showall-value'
              onClick={() => {
                onSelectedTags(false)
              }}
            >
              {messages['maputilities_vehiclelist_hiddenAll']}
            </label>
          </Grid>

          <Grid item xs={12}>
            <GroupsList
              displayCheckOnItems
              highlightId={highlightId}
              showSearch
              defaultExpanded={false}
              loading={loadTags}
              onActionsClicked={onActionsClicked}
              //onGroupCheckChanged={onGroupCheckChanged}
              onGroupCheckChanged={onTagCheckChanged}
              onItemClicked={onItemClicked}
              onExpanded={onExpanded}
              onTitleContainerClicked={onExpanded}
              groupsData={unitTags}
              messageNoItems={messages["maputilities_vehiclelist_no_items"]}
              //onClickSettings={onShowManageVisibleGroups}
              settingsTooltipText={messages["maputilities_vehiclelist_visible_groups"]}
              highlightMessage={messages['maputilities_vehiclelist_follow_unit']}
              noClickEventMessage={messages['maputilities_vehiclelist_no_click_option']}
              bodyHeight="calc(100vh - 330px)"
              overflowY="auto"
            />
          </Grid>
        </Grid>
      }
    </Preferences>
  );
};



export default MapUtilitiesVehicleList;